import eventbus from 'tiny-emitter/instance';
import without from 'lodash/without';
import forEach from 'lodash/forEach';
import reduce from 'lodash/reduce';
import first from 'lodash/first';
import last from 'lodash/last';
import invokeMap from 'lodash/invokeMap';
import throttle from 'lodash/throttle';

import { getAngle } from '@/core/drawing/utils';
import { TYPE, SEAT } from '@/constants';
import Model, { ModelParams } from './model';
import { Seat } from './seat';

import SVG from 'svg.js';
import { RENDERER_MODE } from '@/constants';
import { Block } from './block';
import { SomeRequired } from '../typeHelper';

export class Row extends Model {
  block: Block;
  seats: Seat[] = [];
  throttledRecalculation: any;
  step: number = 0;
  declare seattoremove: Seat;
  declare label: SVG.G;
  declare leftLabel: SVG.Text;
  declare rightLabel: SVG.Text;

  constructor(params: SomeRequired<ModelParams, 'block'>) {
    super(params);
    this.block = params.block;
    this.seats = [];
    this.initView();
    if (this.block) {
      this.block.addRow(this);
    }
    this.setupEventListeners();
    this.throttledRecalculation = throttle(this.recalculateBasisWeights, 250);
  }

  get type() {
    return TYPE.ROW;
  }

  get seatsCount() {
    return this.seats.length;
  }

  get firstSeat() {
    return first(this.seats)!;
  }

  get lastSeat() {
    return last(this.seats)!;
  }

  get initialLength() {
    return this.attributes.initialLength;
  }

  set initialLength(val) {
    this.attributes.initialLength = val;
  }

  get initialSeatsCount() {
    return this.attributes.initialSeatsCount;
  }

  set initialSeatsCount(val) {
    this.attributes.initialSeatsCount = val;
  }

  // @ts-expect-error
  get sid() {
    return this.block.sid;
  }

  updateDynamicElements() {
    // this.repositionLabel();
    invokeMap(this.seats, 'updateDynamicElements');
  }

  setupEventListeners() {
    eventbus.on('seatmap:labels:show', this.showLabel, this);
    eventbus.on('seatmap:labels:hide', this.hideLabel, this);
    eventbus.on('drag:start', this.onSimpleTransformStart, this);
    eventbus.on('drag:end', this.onSimpleTransformEnd, this);
    eventbus.on('rotate:start', this.onSimpleTransformStart, this);
    eventbus.on('rotate:end', this.onSimpleTransformEnd, this);
    if (!this.app.props.isHideRowLabels) {
      eventbus.on('row:renderLabel', this.renderLabel, this);
    }
  }

  removeEventListeners() {
    eventbus.off('seatmap:labels:show', this.showLabel);
    eventbus.off('seatmap:labels:hide', this.hideLabel);
    eventbus.off('drag:start', this.onSimpleTransformStart);
    eventbus.off('drag:end', this.onSimpleTransformEnd);
    eventbus.off('rotate:start', this.onSimpleTransformStart);
    eventbus.off('rotate:end', this.onSimpleTransformEnd);
    if (!this.app.props.isHideRowLabels) {
      eventbus.off('row:renderLabel', this.renderLabel);
    }
  }

  onSimpleTransformStart() {
    this.hideLabel();
  }

  onSimpleTransformEnd() {
    // this.renderLabel();
    if (this.app.showLabels) this.showLabel();
  }

  select() {
    forEach(this.seats, (seat) => seat.select());
  }

  deselect() {
    forEach(this.seats, (seat) => seat.deselect());
  }

  addSeat(seat: Seat) {
    this.view.add(seat.view);
    this.seats.push(seat);
  }

  removeSeat(seat: Seat) {
    this.seats = without(this.seats, seat);
    this.seattoremove = seat;
    if (this.seats.length === 0) {
      this.throttledRecalculation.cancel();
      this.destroy(false);
    } else {
      this.throttledRecalculation();
    }
  }

  removeLastSeat() {
    this.removeSeat(this.lastSeat);
  }

  recalculateBasisWeights() {
    const count = this.seats.length;
    const newStep = 1 / (count - 1);
    for (let i = 0; i < count; i++) {
      if (this.seats[i]) {
        if (i === 0) {
          this.seats[i].t = 0;
        } else if (i === count - 1) {
          this.seats[i].t = 1;
        } else if (this.seattoremove.t === 0 || this.seattoremove.t === 1) {
          this.seats[i].t = newStep;
          if (this.seats[i - 1]) {
            this.seats[i].t += this.seats[i - 1].t;
          }
        }
      }
    }
    this.step = newStep;
    this.onSimpleTransformEnd();
  }

  skew(gap: number) {
    forEach(this.seats, (seat) => seat.view.transform({ y: gap * seat.pos }, true));
  }
  initView() {
    this.view = new SVG.G().addClass('row-shape');

    if (!this.app.props.isHideRowLabels) {
      this.label = new SVG.G().addClass('row-labels');
      const attr = {
        x: 0,
        y: 0,
        'dominant-baseline': 'central',
        'font-family': 'Roboto',
        'font-style': 'normal',
        'font-size': 10 * this.app.seatmap.scale,
      };
      const leftAttr = Object.assign({}, { 'text-anchor': 'end' }, attr);
      const rightAttr = Object.assign({}, { 'text-anchor': 'start' }, attr);
      this.leftLabel = this.label
        .plain(this.attributes.label)
        .attr(leftAttr)
        .addClass('label row-label row-label-right');
      this.rightLabel = this.label
        .plain(this.attributes.label)
        .attr(rightAttr)
        .addClass('label row-label row-label-left');

      this.view.add(this.label);
    }
  }

  changeLabel(label: string = SEAT.LABEL) {
    label = label.trim();
    this.attributes.label = label;
    this.leftLabel.plain(this.attributes.label);
    this.rightLabel.plain(this.attributes.label);
  }

  clearLabel() {
    this.changeLabel();
  }

  showLabel() {
    // if (this.app.showLabels || (RENDERER_MODE && this.app.props.isHideRowLabels)) this.hideLabel();
    this.label.addTo(this.view);
  }

  hideLabel() {
    this.label.remove();
  }

  // eslint-disable-next-line no-unused-vars
  renderLabel(reposition = true) {
    const seats = RENDERER_MODE ? this.seats.filter((s) => s.attributes.invisible !== true) : this.seats;
    if (seats.length > 0) {
      const [fscx, fscy] = first(seats)!.attributes.position;
      const [lscx, lscy] = last(seats)!.attributes.position;
      const coords = {
        x: fscx,
        y: fscy,
        x2: lscx,
        y2: lscy,
      };
      const theta = getAngle(coords, true);
      const deltaX = SEAT.INNER_RADIUS * this.app.seatmap.scale * Math.cos(theta) - 5;
      const deltaY = SEAT.INNER_RADIUS * this.app.seatmap.scale * Math.sin(theta);
      this.leftLabel.attr({
        x: fscx - deltaX,
        y: fscy - deltaY,
      });
      this.rightLabel.attr({
        x: lscx + deltaX,
        y: lscy + deltaY,
      });
      //if (reposition) this.repositionLabel();
    }
  }

  toJSON() {
    const { type, uuid, attributes } = this;
    attributes.label = attributes.label === SEAT.LABEL ? '' : attributes.label;
    return Row.filteredJSON({
      type,
      uuid,
      attributes,
      seats: invokeMap(this.seats, 'toJSON'),
    });
  }

  rbox() {
    return reduce(this.seats, (acc, item) => acc.merge(item.rbox()) as SVG.RBox, this.firstSeat.rbox());
  }

  destroyView() {
    this.view.remove();
  }

  destroy(destroySeats = true, removeEvents = false) {
    if (removeEvents) this.removeEventListeners();
    if (destroySeats) forEach(this.seats, (seat) => seat.destroy());
    this.seats = [];
    this.destroyView();
    this.block.removeRow(this);
  }

  onScaleChange() {
    this.leftLabel?.font({ size: 10 * this.app.seatmap.scale });
    this.rightLabel?.font({ size: 10 * this.app.seatmap.scale });
  }

  static fromJSON(params: SomeRequired<ModelParams, 'block'>) {
    params.attributes.label = params.attributes.label !== '' ? params.attributes.label : SEAT.LABEL;
    return Row.create(params);
  }

  static create(params: SomeRequired<ModelParams, 'block'>) {
    const attributes = {
      label: SEAT.LABEL,
      leftLabelWidth: '',
      rightLabelWidth: '',
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    const row = new Row(params);
    if (params.seats) {
      forEach(params.seats, (seatParams) => Seat.fromJSON({ row, ...seatParams }));
    }
    return row;
  }
}
